import React from 'react'
// libs
import { DropzoneRootProps, DropzoneInputProps } from 'react-dropzone'
// components
import { BUTTON_LAYOUTS } from 'components/UI'
import AddLinkPopover from '../AddLinkPopover'
// icons
import LinkIcon from '@mui/icons-material/Link'
import CloseIcon from '@mui/icons-material/Close'
import FileIcon from '@mui/icons-material/DescriptionOutlined'
// types
import { Attachment, AttachmentType, LinkData, UploadProgress } from '../../types'
// views
import {
  Hint,
  Item,
  Name,
  Tile,
  Zone,
  Title,
  Texts,
  Anchor,
  Dropzone,
  Progress,
  ActionLink,
  PendingName,
  DropzoneIcon,
  DropzoneText,
  RemoveButton,
  DropzoneContent
} from './views'
// utils
import { normalizeLink } from 'utils/index'
import { formatFileSize, getFileExtension } from '../../utils'

const OFFLINE_HINT = 'The file will be uploaded as soon as the network appears'
const UPLOAD_COMPLETE = 100

const POPOVER = {
  form: { align: 'right' as const, width: 340 },
  compact: { align: 'left' as const, width: 320 }
}

interface Props {
  compact?: boolean
  // risk view (registers) rows have no underline under the value
  hideUnderline?: boolean
  disabled?: boolean
  attachment: Attachment | null
  upload?: UploadProgress | null
  isDragActive: boolean
  getRootProps: (props?: DropzoneRootProps) => DropzoneRootProps
  getInputProps: (props?: DropzoneInputProps) => DropzoneInputProps
  onBrowse: () => void
  onClear: () => void
  onCancelUpload?: () => void
  onAddLink: (data: LinkData) => void
}

const joinHint = (...parts: string[]) => parts.filter(Boolean).join(' · ')

const DocLinkField: React.FC<Props> = ({
  compact,
  hideUnderline,
  disabled,
  attachment,
  upload,
  isDragActive,
  getRootProps,
  getInputProps,
  onBrowse,
  onClear,
  onCancelUpload,
  onAddLink
}) => {
  const anchorRef = React.useRef<HTMLDivElement>(null)

  const [isLinkOpen, setLinkOpen] = React.useState(false)

  const popover = compact ? POPOVER.compact : POPOVER.form

  const openLink = () => setLinkOpen(true)
  const closeLink = () => setLinkOpen(false)

  const renderFormText = () => {
    if (isDragActive) {
      return 'Release to attach the file'
    }

    if (isLinkOpen) {
      return 'Adding link...'
    }

    return (
      <>
        Drop file to attach, <ActionLink onClick={onBrowse}>browse</ActionLink> or{' '}
        <ActionLink onClick={openLink}>add link</ActionLink>
      </>
    )
  }

  const renderFormZone = () => (
    <Dropzone {...getRootProps()} $isDragActive={isDragActive} $isActive={isLinkOpen} $disabled={disabled}>
      <input {...getInputProps()} />
      <DropzoneContent>
        <DropzoneIcon />
        <DropzoneText>{renderFormText()}</DropzoneText>
      </DropzoneContent>
    </Dropzone>
  )

  const renderCompactTitle = () => {
    if (isDragActive) {
      return 'Drop to upload'
    }

    if (isLinkOpen) {
      return 'Adding link...'
    }

    return (
      <>
        Drop file, <ActionLink onClick={onBrowse}>browse</ActionLink> or{' '}
        <ActionLink onClick={openLink}>add link</ActionLink>
      </>
    )
  }

  const renderCompactZone = () => (
    <Zone {...getRootProps()} $disabled={disabled} $isActive={isLinkOpen} $isDragActive={isDragActive}>
      <input {...getInputProps()} />
      <Texts>
        <Title>{renderCompactTitle()}</Title>
      </Texts>
    </Zone>
  )

  const renderRemove = (label: string, onClick: () => void) => (
    <RemoveButton
      $compact={compact}
      layout={BUTTON_LAYOUTS.WHITE}
      aria-label={label}
      title={label}
      data-testid='doc-link-remove'
      onClick={onClick}>
      <CloseIcon />
    </RemoveButton>
  )

  const renderUpload = () => {
    const isChecking = upload.progress >= UPLOAD_COMPLETE
    const status = isChecking ? 'Checking file...' : `Uploading ${upload.progress}%`

    return (
      <Item $compact={compact}>
        <Tile $compact={compact}>
          <FileIcon />
        </Tile>
        <Texts>
          <PendingName>{upload.name}</PendingName>
          {!compact && <Hint>{joinHint(getFileExtension(upload.name), status)}</Hint>}
          <Progress $compact={compact} $value={upload.progress} />
        </Texts>
        {onCancelUpload && renderRemove('Cancel upload', onCancelUpload)}
      </Item>
    )
  }

  const renderFile = () => {
    const name = attachment.name || attachment.fileName
    const isUploaded = !!attachment.url
    const hint =
      isUploaded || attachment._pending
        ? joinHint(getFileExtension(name), formatFileSize(attachment.size))
        : OFFLINE_HINT

    return (
      <>
        <Tile $compact={compact}>
          <FileIcon />
        </Tile>
        <Texts>
          {isUploaded ? (
            <Name href={attachment.url} target='_blank' rel='noreferrer' title={name}>
              {name}
            </Name>
          ) : (
            <PendingName title={name}>{name}</PendingName>
          )}
          {(!compact || !isUploaded) && <Hint>{hint}</Hint>}
        </Texts>
        {!disabled && renderRemove('Remove file', onClear)}
      </>
    )
  }

  const renderLink = () => (
    <>
      <Tile $compact={compact}>
        <LinkIcon />
      </Tile>
      <Texts>
        <Name $isLink href={normalizeLink(attachment.url)} target='_blank' rel='noreferrer' title={attachment.url}>
          {attachment.title || attachment.url}
        </Name>
        {!compact && attachment.title && <Hint>{attachment.url}</Hint>}
      </Texts>
      {!disabled && renderRemove('Remove link', onClear)}
    </>
  )

  const renderContent = () => {
    if (upload) {
      return renderUpload()
    }

    if (!attachment) {
      return compact ? renderCompactZone() : renderFormZone()
    }

    // the filled item stays a drop target (dropping a new file replaces the value), but not a keyboard one
    return (
      <Item
        {...getRootProps({ tabIndex: -1 })}
        $compact={compact}
        $hideUnderline={hideUnderline}
        $isDragActive={isDragActive}>
        <input {...getInputProps()} />
        {attachment.type === AttachmentType.Link ? renderLink() : renderFile()}
      </Item>
    )
  }

  return (
    <Anchor ref={anchorRef}>
      {renderContent()}
      <AddLinkPopover
        open={isLinkOpen}
        anchorEl={anchorRef.current}
        align={popover.align}
        width={popover.width}
        onClose={closeLink}
        onSave={onAddLink}
      />
    </Anchor>
  )
}

export default DocLinkField
